// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import type { AttentionThresholdEntity } from '@/account/api/types';
import {
  getAttentionThresholdReasonCopy,
  getAttentionThresholdSettingKey,
} from '@/account/constants/attention-threshold-labels';
import type {
  CachedAttentionThresholdItem,
  CachedAttentionThresholdListProperty,
} from '@/audit/api/types';
import type { AttentionReason } from '@/constants/attention';
import { pluralize } from '@/utilities/pluralize';
import { Box, Stack, Typography } from '@mui/material';

interface Props {
  property: CachedAttentionThresholdListProperty;
}

const ATTENTION_THRESHOLD_TIER_ROWS: {
  field: 'noticeDays' | 'warningDays' | 'criticalDays';
  label: string;
}[] = [
  { field: 'noticeDays', label: 'Notice' },
  { field: 'warningDays', label: 'Warning' },
  { field: 'criticalDays', label: 'Critical' },
];

function formatDays(days: number): string {
  return pluralize(days, { singular: 'day' });
}

/**
 * Displays attention threshold changes in the audit log.
 */
export function AuditAttentionThresholdListLabel(props: Props) {
  const delta = props.property.newValue;

  if (!delta) {
    return null;
  }

  // Flatten all changes into a single list
  const changes: {
    entity: AttentionThresholdEntity;
    reason: AttentionReason;
    label: string;
    action: string;
    oldValue?: CachedAttentionThresholdItem;
    newValue?: CachedAttentionThresholdItem;
  }[] = [];

  // Added overrides
  delta.added?.forEach((item) => {
    const copy = getAttentionThresholdReasonCopy(item);
    changes.push({
      entity: item.entity,
      reason: item.reason,
      label: copy.label,
      action: 'added',
      newValue: item,
    });
  });

  // Removed overrides (reset to the shipped defaults)
  delta.removed?.forEach((item) => {
    const copy = getAttentionThresholdReasonCopy(item);
    changes.push({
      entity: item.entity,
      reason: item.reason,
      label: copy.label,
      action: 'reset to default',
    });
  });

  // Updated overrides
  delta.updated?.forEach((item) => {
    const copy = getAttentionThresholdReasonCopy(item.new);
    changes.push({
      entity: item.new.entity,
      reason: item.new.reason,
      label: copy.label,
      action: 'updated',
      oldValue: item.old,
      newValue: item.new,
    });
  });

  if (changes.length === 0) {
    return null;
  }

  return (
    <Stack>
      <Typography component="span" variant="caption" fontWeight="bold">
        {props.property.name}:
      </Typography>
      {changes.map(({ entity, reason, label, action, oldValue, newValue }) => (
        <Stack
          key={getAttentionThresholdSettingKey({ entity, reason })}
          marginLeft={2}
          spacing={0.25}
        >
          <Typography variant="caption">
            <Typography component="span" variant="inherit">
              {label}:{' '}
            </Typography>
            <Typography
              component="span"
              variant="inherit"
              sx={{ textDecoration: 'underline' }}
            >
              {action}
            </Typography>
          </Typography>
          {newValue !== undefined && (
            <Box component="ul" sx={{ marginY: 0, paddingLeft: 2.5 }}>
              {ATTENTION_THRESHOLD_TIER_ROWS.map((tier) => (
                <Typography
                  key={tier.field}
                  component="li"
                  variant="caption"
                  sx={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {tier.label}:{' '}
                  {oldValue === undefined
                    ? formatDays(newValue[tier.field])
                    : `${formatDays(oldValue[tier.field])} → ${formatDays(
                        newValue[tier.field]
                      )}`}
                </Typography>
              ))}
            </Box>
          )}
        </Stack>
      ))}
    </Stack>
  );
}
